import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Upload, Filter, Eye, Check, X, Copy, ExternalLink,
  Loader2, Clock, CheckCircle, XCircle, Wallet, Bitcoin
} from 'lucide-react';

export default function AdminWithdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [cryptoFilter, setCryptoFilter] = useState('all');
  
  // Modals
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (cryptoFilter !== 'all') params.crypto_type = cryptoFilter;
      
      const response = await adminAPI.getWithdrawals(params);
      setWithdrawals(response.data.withdrawals || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to fetch withdrawals');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [statusFilter, cryptoFilter]);

  const handleViewDetails = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setViewModalOpen(true);
  };

  const handleAction = (withdrawal, type) => {
    setSelectedWithdrawal(withdrawal);
    setActionType(type);
    setAdminNotes('');
    setActionModalOpen(true);
  };

  const executeAction = async () => {
    try {
      setActionLoading(true);
      const status = actionType === 'approve' ? 'completed' : 'rejected';
      await adminAPI.updateWithdrawal(selectedWithdrawal.id, {
        status,
        admin_notes: adminNotes
      });
      toast.success(`Withdrawal ${status === 'completed' ? 'approved' : 'rejected'} successfully`);
      setActionModalOpen(false);
      fetchWithdrawals();
    } catch (error) {
      toast.error(`Failed to ${actionType} withdrawal`);
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Address copied to clipboard');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return styles[status] || styles.pending;
  };

  const getCryptoIcon = (crypto) => {
    const colors = {
      BTC: 'text-orange-400',
      ETH: 'text-blue-400',
      USDT: 'text-emerald-400'
    };
    return colors[crypto] || 'text-gray-400';
  };

  const statCards = [
    { label: 'Total Withdrawals', value: stats.total || 0, icon: Upload, color: 'from-cyan-500 to-blue-500' },
    { label: 'Pending', value: stats.pending || 0, icon: Clock, color: 'from-yellow-500 to-orange-500' },
    { label: 'Completed', value: stats.completed || 0, icon: CheckCircle, color: 'from-emerald-500 to-green-500' },
    { label: 'Rejected', value: stats.rejected || 0, icon: XCircle, color: 'from-red-500 to-pink-500' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Withdrawal Management</h1>
          <p className="text-gray-400 text-sm mt-1">Review and process user withdrawal requests</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-orange-400">${(stats.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          <p className="text-sm text-gray-400">Total Completed</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4"
          >
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${stat.color} flex items-center justify-center mb-3`}>
              <stat.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-white">{stat.value}</p>
            <p className="text-xs text-gray-400">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 sm:flex-none sm:w-48">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full pl-10 pr-8 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500 transition-colors"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="relative flex-1 sm:flex-none sm:w-48">
          <Bitcoin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={cryptoFilter}
            onChange={(e) => setCryptoFilter(e.target.value)}
            className="w-full pl-10 pr-8 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500 transition-colors"
            data-testid="crypto-filter"
          >
            <option value="all">All Crypto</option>
            <option value="BTC">Bitcoin (BTC)</option>
            <option value="ETH">Ethereum (ETH)</option>
            <option value="USDT">Tether (USDT)</option>
          </select>
        </div>
      </div>

      {/* Withdrawals Table - Desktop */}
      <div className="hidden lg:block bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Crypto</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Wallet Address</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
                    <p className="text-gray-400 mt-2">Loading withdrawals...</p>
                  </td>
                </tr>
              ) : withdrawals.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Upload className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No withdrawals found</p>
                  </td>
                </tr>
              ) : (
                withdrawals.map((withdrawal) => (
                  <tr key={withdrawal.id} className="hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white font-medium">{withdrawal.user_name || 'Unknown'}</p>
                        <p className="text-gray-400 text-sm">{withdrawal.user_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <span className="text-orange-400 font-semibold">${(withdrawal.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        {withdrawal.fee > 0 && (
                          <span className="text-gray-500 text-xs block">Fee: ${withdrawal.fee}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${getCryptoIcon(withdrawal.crypto_type)}`}>
                        {withdrawal.crypto_type || 'BTC'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-300 font-mono text-xs">
                          {withdrawal.wallet_address?.substring(0, 12)}...{withdrawal.wallet_address?.slice(-8)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(withdrawal.wallet_address)}
                          className="p-1 text-gray-400 hover:text-cyan-400"
                          title="Copy address"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(withdrawal.status)}`}>
                        {withdrawal.status?.toUpperCase() || 'PENDING'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {withdrawal.created_at ? new Date(withdrawal.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDetails(withdrawal)}
                          className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {withdrawal.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleAction(withdrawal, 'approve')}
                              className="p-2 text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors"
                              title="Approve"
                              data-testid={`approve-withdrawal-${withdrawal.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAction(withdrawal, 'reject')}
                              className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Reject"
                              data-testid={`reject-withdrawal-${withdrawal.id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Withdrawals Cards - Mobile/Tablet */}
      <div className="lg:hidden space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
            <p className="text-gray-400 mt-2">Loading withdrawals...</p>
          </div>
        ) : withdrawals.length === 0 ? (
          <div className="text-center py-12">
            <Upload className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No withdrawals found</p>
          </div>
        ) : (
          withdrawals.map((withdrawal) => (
            <motion.div
              key={withdrawal.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-white font-semibold">{withdrawal.user_name || 'Unknown'}</p>
                  <p className="text-gray-400 text-sm">{withdrawal.user_email}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(withdrawal.status)}`}>
                  {withdrawal.status?.toUpperCase() || 'PENDING'}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <p className="text-gray-500">Amount</p>
                  <p className="text-orange-400 font-semibold">${(withdrawal.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  {withdrawal.fee > 0 && (
                    <p className="text-gray-500 text-xs">Fee: ${withdrawal.fee}</p>
                  )}
                </div>
                <div>
                  <p className="text-gray-500">Crypto</p>
                  <p className={`font-medium ${getCryptoIcon(withdrawal.crypto_type)}`}>{withdrawal.crypto_type || 'BTC'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500">Wallet Address</p>
                  <div className="flex items-center gap-2">
                    <p className="text-gray-300 font-mono text-xs truncate">{withdrawal.wallet_address}</p>
                    <button
                      onClick={() => copyToClipboard(withdrawal.wallet_address)}
                      className="p-1 text-gray-400 hover:text-cyan-400"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="text-gray-300">{withdrawal.created_at ? new Date(withdrawal.created_at).toLocaleDateString() : '-'}</p>
                </div>
              </div>
              
              <div className="flex gap-2 pt-3 border-t border-gray-700/50">
                <button
                  onClick={() => handleViewDetails(withdrawal)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-gray-700/50 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
                >
                  <Eye className="w-4 h-4" /> Details
                </button>
                {withdrawal.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleAction(withdrawal, 'approve')}
                      className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
                    >
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={() => handleAction(withdrawal, 'reject')}
                      className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" /> Reject
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* View Details Modal */}
      <AnimatePresence>
        {viewModalOpen && selectedWithdrawal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setViewModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-gray-800">
                <h2 className="text-xl font-bold text-white">Withdrawal Details</h2>
                <button
                  onClick={() => setViewModalOpen(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(selectedWithdrawal.status)}`}>
                    {selectedWithdrawal.status?.toUpperCase()}
                  </span>
                </div>
                
                <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-xl p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Withdrawal Amount</p>
                  <p className="text-3xl font-bold text-orange-400">
                    ${(selectedWithdrawal.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className={`text-sm mt-1 ${getCryptoIcon(selectedWithdrawal.crypto_type)}`}>
                    {selectedWithdrawal.crypto_type || 'BTC'}
                  </p>
                  {selectedWithdrawal.fee > 0 && (
                    <p className="text-gray-500 text-sm mt-1">Fee: ${selectedWithdrawal.fee}</p>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-700/50">
                    <span className="text-gray-400">User</span>
                    <span className="text-white">{selectedWithdrawal.user_name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-700/50">
                    <span className="text-gray-400">Email</span>
                    <span className="text-cyan-400">{selectedWithdrawal.user_email}</span>
                  </div>
                  <div className="py-2 border-b border-gray-700/50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-400">Wallet Address</span>
                      <button
                        onClick={() => copyToClipboard(selectedWithdrawal.wallet_address)}
                        className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 text-sm"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <p className="text-white font-mono text-sm break-all">{selectedWithdrawal.wallet_address}</p>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-700/50">
                    <span className="text-gray-400">Date</span>
                    <span className="text-white">
                      {selectedWithdrawal.created_at ? new Date(selectedWithdrawal.created_at).toLocaleString() : '-'}
                    </span>
                  </div>
                  {selectedWithdrawal.admin_notes && (
                    <div className="py-2">
                      <p className="text-gray-400 mb-1">Admin Notes</p>
                      <p className="text-white">{selectedWithdrawal.admin_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Approve/Reject Action Modal */}
      <AnimatePresence>
        {actionModalOpen && selectedWithdrawal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setActionModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  actionType === 'approve' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                }`}>
                  {actionType === 'approve' ? (
                    <Check className="w-8 h-8 text-emerald-400" />
                  ) : (
                    <X className="w-8 h-8 text-red-400" />
                  )}
                </div>
                <h2 className="text-xl font-bold text-white mb-2">
                  {actionType === 'approve' ? 'Approve' : 'Reject'} Withdrawal?
                </h2>
                <p className="text-gray-400">
                  {actionType === 'approve' 
                    ? `Confirm sending $${selectedWithdrawal.amount?.toLocaleString()} to ${selectedWithdrawal.user_name}'s wallet.`
                    : `This will reject the withdrawal and refund $${(selectedWithdrawal.amount + (selectedWithdrawal.fee || 0))?.toLocaleString()} to ${selectedWithdrawal.user_name}'s balance.`
                  }
                </p>
                
                {actionType === 'approve' && (
                  <div className="mt-4 p-3 bg-gray-700/50 rounded-xl text-left">
                    <p className="text-gray-400 text-xs mb-1">Send to:</p>
                    <p className="text-white font-mono text-sm break-all">{selectedWithdrawal.wallet_address}</p>
                  </div>
                )}
              </div>
              
              <div className="px-6 pb-6">
                <label className="block text-sm text-gray-400 mb-2">Admin Notes (optional)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={actionType === 'approve' ? 'Transaction ID or notes...' : 'Reason for rejection...'}
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white resize-none focus:outline-none focus:border-cyan-500"
                  data-testid="admin-notes-input"
                />
              </div>
              
              <div className="p-6 border-t border-gray-700 flex gap-3">
                <button
                  onClick={() => setActionModalOpen(false)}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeAction}
                  disabled={actionLoading}
                  className={`flex-1 py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-white ${
                    actionType === 'approve'
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600'
                      : 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600'
                  }`}
                  data-testid="confirm-action-btn"
                >
                  {actionLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : actionType === 'approve' ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <X className="w-5 h-5" />
                  )}
                  {actionType === 'approve' ? 'Approve Withdrawal' : 'Reject & Refund'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
